<template>
  <div class="content-page">
    <div class="loading-completed">
      <TheBreadcrumb :pages="arianeRoute" />
      <NuxtLazyHydrate when-idle>
        <HeaderContent
          v-if="!source"
          :page="currentPageInfo"
        />
        <HeaderSource
          v-else
          :page="currentPageInfo"
        />
      </NuxtLazyHydrate>
      <div class="post-container">
        <div
          v-if="currentPageInfo.favicon && source"
          class="favicon"
        >
          <img
            :src="currentPageInfo.favicon.url"
            :alt="`favicon de ${currentPageInfo.title}`"
          />
        </div>
        <PostItem
          :source="source"
          :side-content="sideContent"
          :post-content="postContent"
        />
      </div>
      <BlueSection
        :connected="authenticated"
        diagonal="none"
        class="blue-section-item"
        :challenges="monthlyChallenges"
      />
      <DoNotMissSection
        v-if="doNotMiss.length"
        :slider-items="doNotMiss"
      />
      <OfferSection
        v-if="offer"
        :offer="offer"
      >
        Acheter ce jeu
      </OfferSection>
      <section
        v-if="otherCommunities.length > 0"
        class="other-communities section"
      >
        <div class="title-line line">
          <h2 class="title-strat">ces communautés peuvent vous intéresser</h2>
          <NuxtLink
            to="/communautes"
            class="button-more"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 19.328 34.43"
            >
              <path
                d="M18.71 15.72 3.66.62a2.178 2.178 0 0 0-3.041 0 2.178 2.178 0 0 0 0 3.041l13.555 13.555L.619 30.77a2.178 2.178 0 0 0 0 3.041 2.178 2.178 0 0 0 3.041 0l15.05-15.05a2.178 2.178 0 0 0 0-3.041Z"
                fill="#fff"
                fill-rule="evenodd"
              />
            </svg>
            Plus de communautés
          </NuxtLink>
        </div>
        <div class="slider">
          <CommunitySlider :communities="otherCommunities" />
        </div>
      </section>
      <p class="mention">
        * Récompense provisoire sous réserve de maintenir votre place au
        classement jusqu'à la fin du mois.
      </p>
    </div>
    <PegiWarning v-if="trick?.community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
import type { Category4 } from '~/types'

definePageMeta({
  middleware: async (to) => {
    const loadCommunityTricks = useLoadCommunityTrick()
    const redirectMovedPost = useRedirectMovedPost()
    return (await loadCommunityTricks(to)) ?? redirectMovedPost(to)
  },
})

const challengeStore = useChallengesStore()
const { monthlyChallenges } = storeToRefs(challengeStore)
const socialStore = useSocialStore()
const { social } = storeToRefs(socialStore)
const doNotMissStore = useDoNotMissStore()
const { doNotMiss } = storeToRefs(doNotMissStore)

const trickStore = useTricksStore()
const { otherTricks, trick, communityTricksSubcategoriesList } =
  storeToRefs(trickStore)

const route = useRoute()
// Improvement: Redirect in the middleware
if (
  trick.value?.category.slug !== 'astuces' ||
  trick.value?.community?.slug !== route.params.communityName ||
  trick.value.sub_category?.slug !== route.params.subCategoryName
) {
  throw createError({ statusCode: usePostGoneState().value ? 410 : 404 })
}

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: trick.value?.community?.title,
      route: `/communautes/${trick.value?.community?.slug}`,
    },
    {
      title: trick.value?.category.name,
      route: `/communautes/${trick.value?.community?.slug}/${trick.value?.category.slug}`,
    },
    {
      title: trick.value?.title,
      route: '',
    },
  ]
})

const source = ref(trick.value?.type === 'source')

const newsArticleSchema = useNewsArticleSchema({
  post: trick,
  source,
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: trick.value?.title_seo,
  script: [newsArticleSchema, breadcrumbSchema],
})

useSeoMeta({
  // posts titled with a pasted URL or web search (Post::isSeoThin): kept out of Google
  robots: trick.value?.seo_noindex ? 'noindex, follow' : undefined,
  title: trick.value?.title_seo,
  description: trick.value?.description_seo,
  keywords: trick.value?.keywords_seo,
  ogTitle: trick.value?.title_og,
  ogType: 'article',
  articlePublishedTime: trick.value?.created_at,
  articleModifiedTime: trick.value?.updated_at || trick.value?.created_at,
  articleAuthor: trick.value?.user?.nickname
    ? [trick.value.user.nickname]
    : undefined,
  ogDescription: trick.value?.description_og,
  ogImage: trick.value?.image_og
    ? trick.value?.image_og.url
    : 'https://www.weareplaystation.fr/img/og-img.jpg',
})

const currentPageInfo = computed(() => {
  const rawTrick = unref(trick)

  const data = {
    title: rawTrick?.title,
    type: rawTrick?.category.name,
    contentType: rawTrick?.type,
    pegi: rawTrick?.community?.pegi,
    image: rawTrick?.image?.url,
    imageLegend: rawTrick?.image_legend,
    favicon: undefined,
    content: undefined,
    subject: undefined,
  }

  if (rawTrick?.type === 'source') {
    return {
      ...data,
      favicon: rawTrick?.favicon,
      urlVideo: rawTrick?.embed,
    }
  } else {
    return {
      ...data,
      subject: rawTrick?.community?.title,
      urlVideo: rawTrick?.video,
      content: rawTrick?.content,
    }
  }
})
const slug = computed(() => route.params.postTitle as string)

const popoutStore = usePopoutStore()
const { popout } = popoutStore

const postContent = computed(() => {
  const rawTrick = unref(trick)
  const rawSocial = unref(social)

  return {
    comments: rawSocial.comments,
    commentsCount: rawSocial.comments_count,
    content: rawTrick?.content,
    contentEmbed: rawTrick?.content_embed,
    contentImages: rawTrick?.content_images,
    createdAt: rawTrick?.created_at,
    updatedAt: rawTrick?.updated_at,
    sourceAuthor: rawTrick?.source_author,
    sourceAuthorUrl: rawTrick?.source_author_url,
    description: rawTrick?.description,
    firstTag: {
      title: rawTrick?.community?.title,
      slug: rawTrick?.community?.slug,
    },
    followed: social.value.is_following,
    has_more_comments: rawSocial.has_more_comments,
    is_liked: rawSocial.is_liked,
    likes: rawSocial.likes_count,
    pegi: rawTrick?.community?.pegi,
    shares: rawSocial.shares_count,
    slug: slug.value,
    slugType: rawTrick?.category.slug,
    tags: rawTrick?.tags,
    title: rawTrick?.title,
    type: rawTrick?.category.name,
    url: rawTrick?.link,
    urlVideo: rawTrick?.video,
    user: rawTrick?.user,
    popout,
  }
})

const { offer, contentBlocksType } = useOffer(trick)

const sideContent = computed(() => {
  const navSide: Category4[] = [{ name: 'La communauté', slug: '' }]

  contentBlocksType.value?.forEach((element) => {
    if (element?.category) {
      const categorySlug = element.category.slug
      if (
        categorySlug === 'actualites-communaute' ||
        categorySlug === 'avis-joueurs' ||
        categorySlug === 'avis-medias' ||
        categorySlug === 'le-coin-des-wapers' ||
        categorySlug === 'astuces'
      ) {
        navSide.push(element.category)
      }
    } else if (element?.slug === 'wapers') {
      navSide.push({ name: 'Les Wapers influents', slug: 'wapers' })
    }
  })

  return {
    title: trick.value?.community?.title,
    type: trick.value?.category.name,
    route: `/communautes/${trick.value?.community?.slug}`,
    nav: navSide,
    cards: otherTricks.value,
    navName: 'astuces',
    subCategories: communityTricksSubcategoriesList.value,
    subCategoriesFilters: true,
  }
})

const otherCommunities = computed(() => {
  const communityBlock = contentBlocksType.value?.find(
    (block) => block?.slug === 'communautes',
  )

  if (!communityBlock?.communities) return []

  return communityBlock.communities.map((item) => ({
    ...item,
    route: `/communautes/${item.slug}`,
  }))
})

const { authenticated } = useAuthenticated()

const loadTrick = useLoadTricks()
watch(authenticated, () => {
  loadTrick(route)
})
</script>

<style lang="scss" scoped>
.content-page {
  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background: $white;
      border-radius: 4.2rem;

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }
    }
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other-communities {
      padding: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
